import type { AddNumberRequest } from '@fundline/shared/schemas/numbers';
import { AddNumberResponseSchema } from '@fundline/shared/schemas/numbers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { workerApi } from '../../lib/worker-api.ts';

export interface PhoneNumber {
  id: string;
  e164: string;
  provider: string;
  status: string;
  health: string;
  state: string | null;
  merchantCount: number;
  maxPerDay: number;
}

/** The workspace's sending numbers (RLS: every member reads them; only the worker writes). */
export async function fetchNumbers(workspaceId: string): Promise<PhoneNumber[]> {
  const { data, error } = await supabase()
    .from('phone_numbers')
    .select('id, e164, provider, status, health, state, assigned_merchant_count, max_per_day')
    .eq('workspace_id', workspaceId)
    .order('created_at');
  if (error) throw new Error(`Couldn't load numbers: ${error.message}`);
  return data.map((n) => ({
    id: n.id,
    e164: n.e164,
    provider: n.provider,
    status: n.status,
    health: n.health,
    state: n.state,
    merchantCount: n.assigned_merchant_count,
    maxPerDay: n.max_per_day,
  }));
}

export function useNumbersQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.numbers(workspaceId),
    queryFn: () => fetchNumbers(workspaceId),
  });
}

/** Adding a number goes through the worker (it talks to the messaging provider). Admins only. */
export function useAddNumber(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddNumberRequest) =>
      workerApi(`/workspaces/${workspaceId}/numbers`, {
        method: 'POST',
        body: input,
        schema: AddNumberResponseSchema,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.numbers(workspaceId) }),
  });
}
